import { useState, useRef, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';

async function getTurnConfig() {
  try {
    const res = await fetch('/api/auth/turn-credentials');
    const { username, credential } = await res.json();
    return { iceServers: [
      { urls: 'stun:turn.lepb.fr:3478' },
      { urls: 'turn:turn.lepb.fr:3478', username, credential },
      { urls: 'turns:turn.lepb.fr:5349', username, credential }
    ]};
  } catch {
    return { iceServers: [{ urls: 'stun:turn.lepb.fr:3478' }] };
  }
}

export default function useVisio(targetUserId, pseudo) {
  const [visioMode, setVisioMode] = useState(false);
  const [visioStatus, setVisioStatus] = useState('');
  const [callTime, setCallTime] = useState(0);
  const [micMuted, setMicMuted] = useState(false);
  const socketRef = useSocket();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const iceHandlerRef = useRef(null);

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const toggleMic = useCallback(() => {
    if (streamRef.current) streamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setMicMuted(m => !m);
  }, []);

  const stopVisio = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    const socket = socketRef?.current;
    socket?.emit('visio-end', { to: targetUserId });
    socket?.off('visio-answered');
    socket?.off('visio-user-offline');
    socket?.off('visio-refused');
    if (iceHandlerRef.current) { socket?.off('visio-ice', iceHandlerRef.current); iceHandlerRef.current = null; }
    pendingCandidatesRef.current = [];
    setMicMuted(false);
    setVisioMode(false);
    setVisioStatus('');
    setCallTime(0);
  }, [targetUserId, socketRef]);

  const startVisio = useCallback(async () => {
    if (!targetUserId) { setVisioStatus("Impossible d'appeler ce profil"); return; }
    const socket = socketRef?.current;
    console.log('socket connected:', socket?.connected, 'id:', socket?.id);
    if (!socket?.connected) { setVisioStatus('Connexion en cours...'); return; }

    setVisioMode(true);
    setVisioStatus('Appel en cours...');
    setMicMuted(false);
    pendingCandidatesRef.current = [];

    const turnConfig = await getTurnConfig();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const pc = new RTCPeerConnection(turnConfig);
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      pc.onicecandidate = e => {
        if (e.candidate) socket.emit('visio-ice', { to: targetUserId, candidate: e.candidate });
      };
      pc.oniceconnectionstatechange = () => console.log('ICE state:', pc.iceConnectionState);
      pc.ontrack = e => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0];
        setVisioStatus('En appel');
        if (!timerRef.current) timerRef.current = setInterval(() => setCallTime(t => t + 1), 1000);
      };

      const iceHandler = async ({ candidate }) => {
        const _pc = pcRef.current;
        if (_pc && _pc.remoteDescription && _pc.remoteDescription.type) {
          try { await _pc.addIceCandidate(candidate); } catch (err) { console.error('addIce error', err); }
        } else {
          pendingCandidatesRef.current.push(candidate);
        }
      };
      iceHandlerRef.current = iceHandler;
      socket.off('visio-ice', iceHandler);
      socket.on('visio-ice', iceHandler);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('visio-call', { to: targetUserId, from: socket.id, pseudo, offer });

      socket.off('visio-answered');
      socket.on('visio-answered', async ({ answer }) => {
        const _pc = pcRef.current;
        if (!_pc) return;
        await _pc.setRemoteDescription(answer);
        const buffered = pendingCandidatesRef.current;
        pendingCandidatesRef.current = [];
        for (const c of buffered) {
          try { await _pc.addIceCandidate(c); } catch (err) { console.error('flush addIce error', err); }
        }
      });
      socket.off('visio-user-offline');
      socket.on('visio-user-offline', () => { setVisioStatus('Utilisateur hors ligne'); setTimeout(stopVisio, 2000); });
      socket.off('visio-refused');
      socket.on('visio-refused', () => { setVisioStatus('Appel refuse'); setTimeout(stopVisio, 2000); });

    } catch (e) {
      console.error('startVisio error:', e);
      setVisioStatus('Erreur camera: ' + e.message);
    }
  }, [targetUserId, pseudo, stopVisio, socketRef]);

  return { visioMode, visioStatus, callTime, micMuted, toggleMic, formatTime, localVideoRef, remoteVideoRef, startVisio, stopVisio };
}
